import { ClipOverlay, OverlayType } from '../../reactvideoeditor/pro/types';
import { EDITOR_MIME_TYPES } from '../../constants';
import { getOverlayUniqueId } from './editorUtils';
import { tempKeyMoment } from '../../types/keyMomentTypes';
import { WindowLandmark } from "../../types/editorTypes";
import { IntlShape } from "react-intl";

type Range = [number, number];
function findClosestRangeIndex(ranges: Range[], value: number): number {
    if (ranges.length === 0) return -1;
    let bestIndex = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < ranges.length; i++) {
        const [a, b] = ranges[i];
        const min = Math.min(a, b);
        const max = Math.max(a, b);
        if (value >= min && value <= max) return i;
        const distance = value < min ? min - value : value - max;
        if (distance < bestDistance) { bestDistance = distance; bestIndex = i; }
    }
    return bestIndex;
}

export const processLandmarks = (
    landmarks: [number, number][],
    startTrimPoint: number,
    endTrimPoint: number,
    source: 'keyMoment' | 'clip',
    timelineCursor: number,
    fps: number,
    hidden: boolean = false
): WindowLandmark[] => {
    const windowLandmarks: WindowLandmark[] = [];
    if (!Array.isArray(landmarks)) return windowLandmarks;

    for (const platformWindowLandmark of landmarks) {
        const landMarkStreamTime = platformWindowLandmark[0];
        const landMarkCoordinate = platformWindowLandmark[1];

        if (landMarkStreamTime >= startTrimPoint && landMarkStreamTime <= endTrimPoint) {
            const landMarkTime = source === 'clip'
                ? (timelineCursor / fps) + (landMarkStreamTime - startTrimPoint)
                : landMarkStreamTime;

            windowLandmarks.push({
                time: landMarkTime,
                coordinate: landMarkCoordinate,
                frame: Math.round(landMarkTime * fps),
                offset: -999,
                isHidden: hidden
            });
        }
    }
    return windowLandmarks;
}

export const mapWindowLandmarks = (
    keyMoment: tempKeyMoment,
    startTrimPoint: number,
    endTrimPoint: number,
    source: 'keyMoment' | 'clip',
    cursor: number,
    fps: number
) => {
    const masterCut = keyMoment.cut;
    const standardWindowLandmarks = processLandmarks(masterCut.landmarks, startTrimPoint, endTrimPoint, source, cursor, fps);
    let hiddenWindowLandmarks: WindowLandmark[] = [];

    if (Array.isArray(masterCut.hidden_landmarks)) {
        hiddenWindowLandmarks = processLandmarks(masterCut.hidden_landmarks, startTrimPoint, endTrimPoint, source, cursor, fps, true);
    }
    return standardWindowLandmarks.concat(hiddenWindowLandmarks);
}

export const mapKeyMomentToOverlays = (
    keyMoment: tempKeyMoment,
    source: 'keyMoment' | 'clip',
    startCursor: number,
    currentRow: number,
    intl: IntlShape,
    defaultEyebrow: string,
    fps: number,
    windowLandmarks?: WindowLandmark[]
) => {
    let overlays: ClipOverlay[] = [];
    let currentCursor = startCursor;
    const masterCut = keyMoment.cut;
    const closestIndex = masterCut.stream_reference ? findClosestRangeIndex(masterCut.trim_points, masterCut.stream_reference) : -1;

    for (let trimPointIndex = 0; trimPointIndex < masterCut.trim_points.length; trimPointIndex++) {
        const trimPoint = masterCut.trim_points[trimPointIndex];
        const duration = trimPoint[1] - trimPoint[0];
        const timelineStartTime = source === 'clip' ? currentCursor : (trimPoint[0] * fps);
        const streamReference = closestIndex === trimPointIndex ? masterCut.stream_reference : trimPoint[0] + (trimPoint[1] - trimPoint[0]) / 2;

        let baseEyebrow = keyMoment.eyebrow || defaultEyebrow;
        const trimLabel = masterCut.trim_labels?.[trimPointIndex];
        let targetId = trimLabel || keyMoment.title;
        let defaultMsg = targetId;

        if (targetId === "CARD" && Array.isArray(keyMoment.tags)) {
            if (keyMoment.tags.includes("RedCard")) { targetId = "RedCard"; defaultMsg = "Red Card"; }
            else if (keyMoment.tags.includes("YellowCard")) { targetId = "YellowCard"; defaultMsg = "Yellow Card"; }
        }

        const localizedLabel = intl.formatMessage({ id: targetId, defaultMessage: defaultMsg });
        baseEyebrow += ` ${localizedLabel}`;

        let shortTitle = keyMoment.title;
        try {
            if (trimLabel && trimLabel !== undefined) {
                shortTitle = intl.formatMessage({ id: trimLabel, defaultMessage: defaultMsg });
            }
        } catch (error) { console.error("Error formatting message:", error); }

        if (windowLandmarks) {
            const cutLandmarks = mapWindowLandmarks(keyMoment, trimPoint[0], trimPoint[1], source, currentCursor, fps);
            windowLandmarks.push(...cutLandmarks);
        }

        const overlay: ClipOverlay = {
            id: getOverlayUniqueId(keyMoment.id + Date.now() + trimPointIndex),
            idClip: keyMoment.id,
            idEvent: keyMoment.event_id,
            groupId: keyMoment.id,
            type: OverlayType.VIDEO,
            from: timelineStartTime,
            videoStartTime: trimPoint[0],
            width: 1920,
            height: 1080,
            top: 0,
            left: 0,
            row: currentRow,
            isDragging: false,
            rotation: 0,
            eyebrow: baseEyebrow,
            content: "/assets/images/Serie_BKT.svg",
            src: keyMoment.url,
            durationInFrames: Math.round(duration * fps),
            mimeType: EDITOR_MIME_TYPES.M3U8,
            mediaSrcDuration: keyMoment.duration,
            stream_reference: source === 'clip' ? undefined : streamReference,
            lcut_stream_reference: source === 'clip' && shortTitle.toUpperCase() !== 'REPLAY' ? streamReference : undefined,
            real_stream_reference: masterCut.stream_reference,
            source: source,
            global_seconds: keyMoment.global_seconds,
            trim_label: shortTitle,
            styles: { objectFit: "contain" },
            title: keyMoment.title,
            created_at: keyMoment.created_at,
            updated_at: keyMoment.updated_at,
            play_time: keyMoment.play_time,
            event_time: keyMoment.event!.start_schedule,
            home_team: keyMoment.event?.home_team,
            away_team: keyMoment.event?.away_team
        };

        overlays.push(overlay);
        currentCursor += Math.round(duration * fps);
    }
    return { overlays, nextCursor: currentCursor };
}